/**
 * Public Booking API — Vitrina.
 *
 * Goes through the EDGE (`POST /api/booking/{salon_id}`,
 * VITRINA_BOOKINGS.md §3.4), like everything else the vitrina calls. It used
 * to POST straight to runapis — the single exception in an otherwise
 * fully-proxied client, and the only reason the vitrina needed cross-origin
 * CORS at all. That exception cost us: of the whole domain portfolio only
 * peluquerias.mx was on the origin allow list, so on every other host the
 * customer filled in their name, phone and email, pressed Confirmar, and the
 * browser silently blocked the request (api-salon#404).
 *
 * Deliberately plain `fetch`, not the axios instance: axios is configured with
 * runapis as its baseURL and carries interceptors built for that origin. Using
 * it here would leave the client one refactor away from pointing at runapis
 * again.
 */
import { getVitrinaApiBase } from '@/js/utils/environment.js';

/**
 * Create a booking.
 *
 * @param {string|number} salonId  16-digit salon id
 * @param {Object} bookingData     alias-keyed payload (cfn/cln/cem/ph/smid/bdt/sids/nt)
 * @returns {Promise<Object>}      the API's response body
 * @throws {Error} with `.status` and `.body` set, so callers can tell a taken
 *         slot (409) from a closed salon (404) from a real outage (502).
 */
export async function createBooking(salonId, bookingData) {
  if (!navigator.onLine) {
    throw new Error('No internet connection. Booking operations require connectivity.');
  }

  const res = await fetch(`${getVitrinaApiBase()}/api/booking/${salonId}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(bookingData),
  });

  // Read the body before branching: the edge passes 409's conflict `reason`
  // through verbatim, and that is what lets the wizard say something more
  // useful than "algo salió mal".
  let body = null;
  try {
    body = await res.json();
  } catch {
    body = null;
  }

  if (res.ok) return body;

  const error = new Error(body?.error || body?.detail?.reason || `booking failed (${res.status})`);
  error.status = res.status;
  error.body = body;
  console.error('[bookings] create failed:', res.status, body);
  throw error;
}
